import { useCallback, useMemo, useState } from 'react';

/** Rows per page across the institute list views. One knob, one rhythm. */
export const DEFAULT_PAGE_SIZE = 10;

export interface Paginated<T> {
  /** 1-based and ALREADY clamped to the current page count. */
  page: number;
  /** Never below 1, so callers can divide/compare without guarding. */
  pageCount: number;
  pageItems: T[];
  total: number;
  /** 1-based index of the first visible row; 0 when there are none. */
  rangeStart: number;
  rangeEnd: number;
  setPage: (next: number) => void;
}

/**
 * Client-side paging over a list already held in memory (a TanStack Query
 * result, typically) — the list RPCs return whole sets with no LIMIT.
 *
 * The current page is DERIVED, never stored. Archiving or deleting the last
 * row on the last page shortens the list under the user's feet; a stored page
 * index would then slice past the end and strand them on a blank page.
 */
export function usePagination<T>(items: readonly T[], pageSize = DEFAULT_PAGE_SIZE): Paginated<T> {
  const [requested, setRequested] = useState(1);

  const size = Math.max(1, pageSize);
  const total = items.length;
  const pageCount = Math.max(1, Math.ceil(total / size));
  const page = Math.min(Math.max(1, requested), pageCount);

  const pageItems = useMemo(() => items.slice((page - 1) * size, page * size), [items, page, size]);

  const setPage = useCallback((next: number) => {
    setRequested(Math.max(1, Math.trunc(next)));
  }, []);

  return {
    page,
    pageCount,
    pageItems,
    total,
    rangeStart: total === 0 ? 0 : (page - 1) * size + 1,
    rangeEnd: Math.min(page * size, total),
    setPage,
  };
}
